export interface ShippingOriginSchedule {
  id: string
  name: string
  city: string
  region: string
  timezone: string
  weekdays: number[]
  dispatchTimes: string[]
  /** Minimum packing time before an order can be handed to the next run. */
  preparationMinutes: number
  updatedAt?: string
}

export interface DispatchWindow {
  localDate: string
  time: string
  dayOffset: number
}

export interface ShippingSettings {
  origins: ShippingOriginSchedule[]
}

export const SHIPPING_TIMEZONES = [
  { value: 'Europe/Kaliningrad', label: 'Калининградская область — UTC+2' },
  { value: 'Europe/Moscow', label: 'Москва и Санкт-Петербург — UTC+3' },
  { value: 'Europe/Samara', label: 'Самарская область — UTC+4' },
  { value: 'Asia/Yekaterinburg', label: 'Екатеринбург — UTC+5' },
  { value: 'Asia/Omsk', label: 'Омская область — UTC+6' },
  { value: 'Asia/Novokuznetsk', label: 'Кемеровская область — UTC+7' },
  { value: 'Asia/Irkutsk', label: 'Иркутская область — UTC+8' },
  { value: 'Asia/Yakutsk', label: 'Якутск — UTC+9' },
  { value: 'Asia/Vladivostok', label: 'Владивосток — UTC+10' },
  { value: 'Asia/Magadan', label: 'Магадан — UTC+11' },
  { value: 'Asia/Kamchatka', label: 'Камчатка — UTC+12' },
] as const

export function shippingTimezoneLabel(value: string): string {
  return SHIPPING_TIMEZONES.find((timezone) => timezone.value === value)?.label || value
}

export const DEFAULT_SHIPPING_ORIGIN: ShippingOriginSchedule = {
  id: 'primary',
  name: 'Основная точка отправки',
  city: 'Юрга',
  region: 'Кемеровская область',
  timezone: 'Asia/Novokuznetsk',
  weekdays: [1, 2, 3, 4, 5, 6, 7],
  dispatchTimes: ['10:30', '15:00'],
  preparationMinutes: 60,
}

export const DEFAULT_SHIPPING_SETTINGS: ShippingSettings = {
  origins: [DEFAULT_SHIPPING_ORIGIN],
}

function partsInTimezone(now: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now)
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value)
  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    minutes: value('hour') * 60 + value('minute'),
  }
}

function minutesOfDay(time: string): number {
  const [hour, minute] = time.split(':').map(Number)
  return hour * 60 + minute
}

function isoWeekday(date: Date): number {
  const weekday = date.getUTCDay()
  return weekday === 0 ? 7 : weekday
}

export function nextDispatchWindow(
  schedule: ShippingOriginSchedule,
  now = new Date(),
  options: { inclusive?: boolean } = {},
): DispatchWindow | null {
  if (schedule.weekdays.length === 0 || schedule.dispatchTimes.length === 0) return null
  const local = partsInTimezone(now, schedule.timezone)
  const times = [...schedule.dispatchTimes].sort((left, right) => minutesOfDay(left) - minutesOfDay(right))
  const baseDate = new Date(Date.UTC(local.year, local.month - 1, local.day))

  for (let dayOffset = 0; dayOffset <= 14; dayOffset += 1) {
    const date = new Date(baseDate)
    date.setUTCDate(baseDate.getUTCDate() + dayOffset)
    if (!schedule.weekdays.includes(isoWeekday(date))) continue
    const time = times.find((candidate) => dayOffset > 0 || (
      options.inclusive === false
        ? minutesOfDay(candidate) > local.minutes
        : minutesOfDay(candidate) >= local.minutes
    ))
    if (!time) continue
    return {
      localDate: date.toISOString().slice(0, 10),
      time,
      dayOffset,
    }
  }
  return null
}

/**
 * Finds the first dispatch run that still leaves the configured packing time.
 * A run exactly at the ready minute is intentionally skipped: the team needs
 * the whole configured interval, not a zero-minute handoff margin.
 */
export function nextDispatchWindowForOrder(
  schedule: ShippingOriginSchedule,
  orderCreatedAt: string | Date,
  now = new Date(),
): DispatchWindow | null {
  const createdAt = orderCreatedAt instanceof Date ? orderCreatedAt : new Date(orderCreatedAt)
  const preparationMs = Math.max(0, Math.round(schedule.preparationMinutes || 0)) * 60_000
  const readyAt = Number.isNaN(createdAt.getTime())
    ? now
    : new Date(Math.max(now.getTime(), createdAt.getTime() + preparationMs))
  return nextDispatchWindow(schedule, readyAt, { inclusive: false })
}

export function dispatchWindowLabel(window: DispatchWindow | null): string {
  if (!window) return 'Рейс не запланирован'
  if (window.dayOffset === 0) return `Сегодня в ${window.time}`
  if (window.dayOffset === 1) return `Завтра в ${window.time}`
  const date = new Date(`${window.localDate}T00:00:00Z`)
  const formatted = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' })
  return `${formatted} в ${window.time}`
}
